import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Clock, CheckCircle, Package, Users, AlertTriangle, Globe } from 'lucide-react';

const PlanSelectionModal = ({ isOpen, onClose, plans, onSelectPlan, shipmentItems, clientInfo }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Helper function to calculate relative time
  const getRelativeTime = (dateString) => {
    const now = new Date();
    const planDate = new Date(dateString);
    const diffInSeconds = Math.floor((now - planDate) / 1000);
    
    if (diffInSeconds < 60) {
      return `${diffInSeconds} seconds ago`;
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days === 1 ? '' : 's'} ago`;
    }
  };

  // Calculate differences between current plan and previous plan (chronologically)
  const calculateDifferences = (currentPlan, previousPlan) => {
    if (!previousPlan || !currentPlan.items || !previousPlan.items) {
      return { added: [], removed: [], modified: [] };
    }

    const previousItemsMap = new Map();
    const currentItemsMap = new Map();

    // Create maps for easier comparison
    previousPlan.items.forEach(item => {
      previousItemsMap.set(item.msku, {
        sku: item.msku,
        quantity: item.quantity
      });
    });

    currentPlan.items.forEach(item => {
      currentItemsMap.set(item.msku, {
        sku: item.msku,
        quantity: item.quantity
      });
    });

    const added = [];
    const removed = [];
    const modified = [];

    // Check for items in current plan but not in previous plan (added)
    currentItemsMap.forEach((currentItem, sku) => {
      if (!previousItemsMap.has(sku)) {
        added.push({
          sku: currentItem.sku,
          quantity: currentItem.quantity,
          title: shipmentItems?.find(si => si.item.merchant_sku === sku)?.item.title || 'Unknown Item'
        });
      }
    });

    // Check for items in previous plan but not in current plan (removed) or with different quantities (modified)
    previousItemsMap.forEach((previousItem, sku) => {
      if (!currentItemsMap.has(sku)) {
        removed.push({
          sku: previousItem.sku,
          quantity: previousItem.quantity,
          title: shipmentItems?.find(si => si.item.merchant_sku === sku)?.item.title || 'Unknown Item'
        });
      } else {
        const currentItem = currentItemsMap.get(sku);
        if (currentItem.quantity !== previousItem.quantity) {
          modified.push({
            sku: previousItem.sku,
            title: shipmentItems?.find(si => si.item.merchant_sku === sku)?.item.title || 'Unknown Item',
            previousQuantity: previousItem.quantity,
            currentQuantity: currentItem.quantity,
            difference: currentItem.quantity - previousItem.quantity
          });
        }
      }
    });

    return { added, removed, modified };
  };

  // Sort plans by creation date (most recent first) and find the most recent
  const sortedPlans = [...plans].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const mostRecentPlan = sortedPlans[0];

  // Auto-select the most recent plan when modal opens
  useEffect(() => {
    if (isOpen && mostRecentPlan) {
      setSelectedPlan(mostRecentPlan);
    }
  }, [isOpen, mostRecentPlan]);

  const handleSubmit = () => {
    if (selectedPlan) {
      onSelectPlan(selectedPlan);
      onClose();
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

      return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="!w-[80vw] !max-w-[80vw] max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-slate-900">
            Select Plan for Submission
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-2">
          {sortedPlans.map((plan, index) => {
            const isSelected = selectedPlan?.id === plan.id;
            const isMostRecent = plan.id === mostRecentPlan?.id;
            const isOldest = index === sortedPlans.length - 1; // Since sortedPlans is newest first, last item is oldest
            const previousPlan = index < sortedPlans.length - 1 ? sortedPlans[index + 1] : null; // Previous plan chronologically
            const differences = calculateDifferences(plan, previousPlan);
            const hasChanges = differences.added.length > 0 || differences.removed.length > 0 || differences.modified.length > 0;
            const showDifferences = !isOldest && hasChanges; // Don't show differences for the oldest (original) plan
            
            // Calculate totals
            const planSkuCount = plan.items?.length || 0;
            const planUnitCount = plan.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

            // Check if plan country matches client country
            const planCountry = plan.ship_from_address?.country_code;
            const clientCountryLabel = clientInfo?.country; // 'USA' or 'Canada'
            const expectedPlanCountry = clientCountryLabel === 'USA' ? 'US' : 'CA';
            const isCountryCorrect = planCountry === expectedPlanCountry;
            
            return (
              <div
                key={plan.id}
                onClick={() => setSelectedPlan(plan)}
                className={`
                  relative p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md
                  ${isSelected 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-slate-200 bg-white hover:border-slate-300'
                  }
                  ${isMostRecent ? 'ring-2 ring-green-200 bg-green-50' : ''}
                `}
              >
                {/* Most Recent Badge */}
                {isMostRecent && (
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-medium px-2 py-1 rounded-full">
                    Most Recent
                  </div>
                )}
                
                {/* Original Badge */}
                {isOldest && (
                  <div className="absolute -top-2 -left-2 bg-blue-500 text-white text-xs font-medium px-2 py-1 rounded-full">
                    Original
                  </div>
                )}
                
                {/* Selection Indicator */}
                {isSelected && (
                  <div className="absolute top-3 right-3">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                  </div>
                )}
                
                <div className="space-y-2">
                  {/* Plan Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium text-base text-slate-900">
                        {plan.name || `Plan #${plan.id}`}
                      </div>
                      <div className="text-xs text-slate-500 font-mono space-y-0.5">
                        <div>
                          <span className="font-bold text-slate-700">Inbound Plan ID:</span> {plan.inbound_plan_id || 'N/A'}
                        </div>
                        <div>
                          <span>Internal ID:</span> {plan.id}
                        </div>
                      </div>
                    </div>
                    
                    {/* SKU and Unit Counts */}
                    <div className="flex space-x-2 text-xs">
                      <div className="flex items-center space-x-1 bg-blue-100 px-2 py-1 rounded-full">
                        <Package className="w-3 h-3 text-blue-600" />
                        <span className="font-medium text-blue-800">{planSkuCount} SKUs</span>
                      </div>
                      <div className="flex items-center space-x-1 bg-green-100 px-2 py-1 rounded-full">
                        <Users className="w-3 h-3 text-green-600" />
                        <span className="font-medium text-green-800">{planUnitCount} Units</span>
                      </div>
                      {planCountry && (
                        <div className={`flex items-center space-x-1 px-2 py-1 rounded-full ${
                          isCountryCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          <Globe className="w-3 h-3" />
                          <span className="font-medium">
                            {planCountry} {isCountryCorrect ? '(Correct)' : '(Incorrect)'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Warehouse and Timestamp */}
                  <div className="flex items-center justify-between text-xs text-slate-600">
                    <div className="flex items-center space-x-3">
                      {plan.warehouse_name && (
                        <span><strong>Warehouse:</strong> {plan.warehouse_name}</span>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-1 text-slate-500">
                      <Clock className="w-3 h-3" />
                      <span>{formatDateTime(plan.created_at)}</span>
                      <span className="text-blue-600 font-medium">
                        ({getRelativeTime(plan.created_at)})
                      </span>
                    </div>
                  </div>
                  
                  {/* Differences Section */}
                  {showDifferences && (
                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-center space-x-1 mb-1">
                        <AlertTriangle className="w-3 h-3 text-yellow-600" />
                        <span className="text-xs font-medium text-yellow-800">
                          Changes from Previous Plan:
                        </span>
                      </div>
                      
                      <div className="space-y-1 text-xs">
                                                  {/* Removed Items */}
                          {differences.removed.length > 0 && (
                            <div>
                              <span className="font-medium text-red-700">Removed Items:</span>
                              <ul className="ml-4 space-y-1">
                                {differences.removed.slice(0, 3).map((item, index) => (
                                  <li key={index} className="text-red-600">
                                    • Removed {item.quantity} of {item.sku} ({item.title})
                                  </li>
                                ))}
                                {differences.removed.length > 3 && (
                                  <li className="text-red-600 italic">
                                    ... and {differences.removed.length - 3} more items
                                  </li>
                                )}
                              </ul>
                            </div>
                          )}
                          
                          {/* Added Items */}
                          {differences.added.length > 0 && (
                            <div>
                              <span className="font-medium text-green-700">Added Items:</span>
                              <ul className="ml-4 space-y-1">
                                {differences.added.slice(0, 3).map((item, index) => (
                                  <li key={index} className="text-green-600">
                                    • Added {item.quantity} of {item.sku} ({item.title})
                                  </li>
                                ))}
                                {differences.added.length > 3 && (
                                  <li className="text-green-600 italic">
                                    ... and {differences.added.length - 3} more items
                                  </li>
                                )}
                              </ul>
                            </div>
                          )}
                          
                          {/* Modified Items */}
                          {differences.modified.length > 0 && (
                            <div>
                              <span className="font-medium text-blue-700">Quantity Changes:</span>
                              <ul className="ml-4 space-y-1">
                                {differences.modified.slice(0, 3).map((item, index) => (
                                  <li key={index} className="text-blue-600">
                                    • {item.sku}: {item.previousQuantity} → {item.currentQuantity} 
                                    ({item.difference > 0 ? '+' : ''}{item.difference})
                                  </li>
                                ))}
                                {differences.modified.length > 3 && (
                                  <li className="text-blue-600 italic">
                                    ... and {differences.modified.length - 3} more changes
                                  </li>
                                )}
                              </ul>
                            </div>
                          )}
                      </div>
                    </div>
                  )}
                  
                  {/* No changes indicator - only show for non-oldest plans that have no changes from previous */}
                  {!isOldest && !hasChanges && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center space-x-1">
                        <CheckCircle className="w-3 h-3 text-green-600" />
                        <span className="text-xs font-medium text-green-800">
                          No changes from previous plan
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-3 border-t">
          <Button
            variant="outline"
            onClick={onClose}
            className="px-4 py-2"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedPlan}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2"
          >
            Submit to Selected Plan
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlanSelectionModal; 